// Client JSX runtime — creates live DOM nodes instead of HTML strings.
// When a signal is read during prop/child evaluation, a subscription is
// created so that DOM node updates automatically when the signal changes.
// No virtual DOM: changes are applied directly and surgically.

import { isEventProp, normalizeAttrName, styleEntriesToCss } from './attrs.js';
import { createEffect } from './signal.js';

// Detect server/test environments where the DOM is unavailable.
const IS_SERVER = typeof document === 'undefined';

// Promise<string | object | null> lets OML JSX elements (Promise<OmlNode | string>) pass as children
// without errors — mirrors the same widening in the SSR runtime.
type Child =
  | Node
  | string
  | number
  | boolean
  | null
  | undefined
  | (() => Child)
  | Child[]
  | Promise<string | object | null>;
type StyleObject = Record<string, string | number>;

export type Props = {
  children?: Child | Child[];
  key?: string | number;
  ref?: ((el: Element) => void) | { current: Element | null };
  style?: string | StyleObject;
  className?: string;
  htmlFor?: string;
  [prop: string]: unknown;
};

export type ComponentType<P extends Props = Props> = (props: P) => Node | Promise<string>;

const SVG_NAMESPACE = 'http://www.w3.org/2000/svg';
const SVG_TAGS = new Set([
  'svg',
  'circle',
  'rect',
  'path',
  'line',
  'polyline',
  'polygon',
  'ellipse',
  'g',
  'text',
  'defs',
  'use',
  'symbol',
  'clipPath',
  'mask',
  'filter',
  'linearGradient',
  'radialGradient',
  'stop',
  'pattern',
  'image',
  'foreignObject',
]);

function applyStyle(el: HTMLElement, style: string | StyleObject): void {
  if (typeof style === 'string') {
    el.style.cssText = style;
  } else {
    for (const [prop, value] of styleEntriesToCss(style)) {
      el.style.setProperty(prop, value);
    }
  }
}

function applyProp(el: Element, key: string, value: unknown): void {
  if (key === 'dangerouslySetInnerHTML') {
    (el as HTMLElement).innerHTML = (value as { __html: string }).__html;
    return;
  }

  if (key === 'ref') {
    if (typeof value === 'function') (value as (el: Element) => void)(el);
    else if (value && typeof value === 'object' && 'current' in value) {
      (value as { current: Element | null }).current = el;
    }
    return;
  }

  if (key === 'style') {
    if (value != null) applyStyle(el as HTMLElement, value as string | StyleObject);
    return;
  }

  // Event handlers
  if (isEventProp(key)) {
    const event = key.slice(2).toLowerCase();
    el.addEventListener(event, value as EventListener);
    return;
  }

  const attr = normalizeAttrName(key);

  if (value === false || value == null) {
    el.removeAttribute(attr);
  } else if (value === true) {
    el.setAttribute(attr, '');
  } else {
    el.setAttribute(attr, String(value));
  }
}

function toNode(child: Child): Node | Node[] {
  if (child == null || child === false) return document.createTextNode('');
  if (Array.isArray(child)) return child.flatMap((c) => toNode(c));
  if (child instanceof Node) return child;
  // Promise<string> only occurs when a reactive() component is used in a context
  // where it hasn't been recompiled for the client — unreachable in a correct build.
  if (child instanceof Promise) return document.createTextNode('');
  if (typeof child === 'function') {
    // Reactive child — re-evaluates when signals it reads change.
    // The effect runs synchronously before `anchor` has a parent, so we
    // capture the initial nodes here and return them in a fragment so they
    // are inserted into the DOM immediately. Updates insert before `anchor`.
    const anchor = document.createTextNode('');
    let prevNodes: Node[] = [];
    let initialized = false;

    createEffect(() => {
      const newNodes = [toNode((child as () => Child)())].flat();
      if (!initialized) {
        initialized = true;
        prevNodes = newNodes;
      } else {
        const parent = anchor.parentNode;
        if (parent) {
          for (const n of newNodes) parent.insertBefore(n, anchor);
          for (const n of prevNodes) n.parentNode?.removeChild(n);
          prevNodes = newNodes;
        }
      }
    });

    const frag = document.createDocumentFragment();
    for (const n of prevNodes) frag.append(n);
    frag.append(anchor);
    return frag as unknown as Node;
  }
  return document.createTextNode(String(child));
}

function mountChildren(el: Element, children: Child | Child[] | undefined): void {
  if (children == null) return;

  const flat = Array.isArray(children) ? children : [children];
  for (const child of flat) {
    const nodes = [toNode(child)].flat();
    el.append(...nodes);
  }
}

// ─── Minimal SSR path ────────────────────────────────────────────────────────
// Used when document is unavailable (server render, test environment).
// Reactive function props/children are called once for their initial value,
// matching the behaviour of the server JSX runtime in src/jsx-runtime.ts.

const VOID_TAGS = new Set([
  'area',
  'base',
  'br',
  'col',
  'embed',
  'hr',
  'img',
  'input',
  'link',
  'meta',
  'param',
  'source',
  'track',
  'wbr',
]);

function esc(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

function styleStr(style: StyleObject): string {
  return styleEntriesToCss(style)
    .map(([k, v]) => `${k}:${v}`)
    .join(';');
}

async function ssrChildren(children: Child | Child[] | undefined): Promise<string> {
  if (children == null || children === false) return '';
  if (typeof Node !== 'undefined' && children instanceof Node) return '';
  if (Array.isArray(children)) {
    return (await Promise.all(children.map((c) => ssrChildren(c)))).join('');
  }
  if (children instanceof Promise) return ((await children) as string) ?? '';
  if (typeof children === 'boolean') return '';
  if (typeof children === 'function') return ssrChildren((children as () => Child)());
  return esc(String(children));
}

function ssrAttrs(props: Props): string {
  const out: string[] = [];
  for (const [key, val] of Object.entries(props)) {
    if (key === 'children' || key === 'key' || key === 'ref' || key === 'dangerouslySetInnerHTML')
      continue;
    if (isEventProp(key)) continue;
    const attr = normalizeAttrName(key);
    const v = typeof val === 'function' ? (val as () => unknown)() : val;
    if (v === false || v == null) continue;
    if (v === true) {
      out.push(attr);
      continue;
    }
    if (attr === 'style') {
      out.push(`style="${esc(typeof v === 'string' ? v : styleStr(v as StyleObject))}"`);
      continue;
    }
    out.push(`${attr}="${esc(String(v))}"`);
  }
  return out.length ? ` ${out.join(' ')}` : '';
}

async function ssrJsx(
  type: string | ComponentType,
  props: Props,
  _key?: string | number,
): Promise<string> {
  if (typeof type === 'function') {
    const r = type(props);
    return r instanceof Promise ? await r : (r as unknown as string);
  }
  if (type === Fragment) return ssrChildren(props.children);
  const attrs = ssrAttrs(props);
  if (props.dangerouslySetInnerHTML) {
    return `<${type}${attrs}>${(props.dangerouslySetInnerHTML as { __html: string }).__html}</${type}>`;
  }
  if (VOID_TAGS.has(type)) return `<${type}${attrs}>`;
  return `<${type}${attrs}>${await ssrChildren(props.children)}</${type}>`;
}

// ─────────────────────────────────────────────────────────────────────────────

export function jsx(
  type: string | ComponentType,
  props: Props,
  _key?: string | number,
): Node | Promise<string> {
  if (IS_SERVER) return ssrJsx(type, props, _key);

  if (typeof type === 'function') {
    return type(props) as unknown as Node;
  }

  if (type === Fragment) {
    const frag = document.createDocumentFragment();
    mountChildren(frag as unknown as Element, props.children);
    return frag;
  }

  const el = SVG_TAGS.has(type)
    ? document.createElementNS(SVG_NAMESPACE, type)
    : document.createElement(type);

  for (const [key, value] of Object.entries(props)) {
    if (key === 'children') continue;

    if (typeof value === 'function' && !key.startsWith('on') && key !== 'ref') {
      // Reactive prop — re-run when the signal it reads changes
      createEffect(() => applyProp(el, key, (value as () => unknown)()));
    } else {
      applyProp(el, key, value);
    }
  }

  mountChildren(el, props.children);

  return el;
}

export const jsxs = jsx;
export const jsxDEV = jsx;

export const Fragment = '__Fragment__';

// Client-side JSX namespace. JSX.Element includes Promise<string> so that
// reactive() components and server JSX components are usable inside reactive
// component trees without TypeScript errors. At runtime they always compile to Node.
export namespace JSX {
  export type Element = Node | Promise<string | object | null>;

  /** Reactive child: a signal getter (or any zero-arg fn) re-runs on signal change. */
  export type ReactiveChild = () => Child;
  export type Child =
    | Node
    | string
    | number
    | boolean
    | null
    | undefined
    | ReactiveChild
    | Child[]
    | Promise<string | object | null>;

  export interface ElementChildrenAttribute {
    children: object;
  }

  export interface IntrinsicAttributes {
    key?: string | number;
  }

  interface DOMAttributes {
    children?: Child | Child[];
    [attr: string]: unknown;
  }

  export interface IntrinsicElements {
    [tag: string]: DOMAttributes;
  }
}
